import React, { useMemo } from 'react';
import { useActionCriteria } from '../../utils/useActionProps';
import { useCompany } from '../../utils/FetchConfig';
import { useQuery } from '@apollo/client';
import CustomerAggregateBySalesTransactionCriteriaQuery from '../../graphql/queries/salesLedger/CustomerAggregateBySalesTransactionCriteriaQuery.graphql';
import { CustomTotalCard } from '../../components/Workspace/TotalsView';
import {
  ICustomerAggregateBySalesTransactionQueryResult,
  ICustomerAggregateBySalesTransactionQueryVariables,
} from '../../graphql/schema/p4types';

/**
 * Component to display customer balances matching the criteria of the parent Sales Transactions Workspace.
 */
const SalesTransactionBalanceCards = () => {
  const salesTransactionActionCriteria = useActionCriteria();
  //We don't actually care about checkbox selections for this display, so extra the filters only.
  const ignoreSelectionsCriteria = useMemo(
    () => ({
      filters: salesTransactionActionCriteria.filters,
      include: false,
      actionEntities: [],
    }),
    [salesTransactionActionCriteria.filters]
  );

  const company = useCompany();

  const balanceColumns = useMemo(
    () => ({
      'balance.current': {
        name: company?.currentFinancialPeriod?.name ?? 'Current',
      },
      'balance.month1': {
        name: company?.month1FinancialPeriod?.name ?? 'Month +1',
      },
      'balance.month2': {
        name: company?.month2FinancialPeriod?.name ?? 'Month +2',
      },
      'balance.month3': {
        name: company?.month3FinancialPeriod?.name ?? 'Month +3',
      },
      'balance.month4': {
        name: company?.month4FinancialPeriod?.name ?? 'Month +4',
      },
    }),
    [company]
  );

  const { data } = useQuery<
    ICustomerAggregateBySalesTransactionQueryResult,
    ICustomerAggregateBySalesTransactionQueryVariables
  >(CustomerAggregateBySalesTransactionCriteriaQuery, {
    variables: {
      salesTransactionActionCriteria: ignoreSelectionsCriteria,
      aggregateCriteria: {
        sumColumns: Object.keys(balanceColumns),
      },
    },
  });

  return useMemo(() => {
    const cards = Object.keys(balanceColumns).map((balanceKey) => {
      const totalColumn = data?.listData?.totalColumns?.find((totalColumn) => totalColumn?.columnName === balanceKey);
      const column = balanceColumns[balanceKey];

      return (
        <CustomTotalCard
          key={balanceKey}
          label={column.name}
          value={totalColumn?.sum ?? 0}
          hint={'Sum of balances for customers per the current transaction workspace filters.'}
          styles={{ container: { borderBottom: '3px solid #D0EDFF' } }}
        />
      );
    });
    return <>{cards}</>;
  }, [balanceColumns, data]);
};

export default SalesTransactionBalanceCards;
